import { useState } from 'react';
import { BoutonDocument } from '../../components/BoutonDocument.jsx';
import { SignPad } from '../../components/SignPad.jsx';
import { SOC } from '../../constants/company';
import { dateHeureSuisse } from '../../lib/acces';
import {
  dispositionsFinales,
  dureeDeterminee,
  phraseHoraire,
  phraseJournee,
} from '../../lib/contrat';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { printDoc } from '../../lib/print';
import { C, docAcc } from '../../theme/palette';

/**
 * Signature du contrat par l'employé, au bas de sa lecture.
 *
 * <p>Il coche qu'il a lu et accepte, signe au doigt, valide : le serveur fige
 * le PDF signé dans son dossier. Il ne change rien aux conditions — elles
 * s'affichent au-dessus, telles que la direction les a fixées.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.contrat état de signature rendu par le serveur
 * @param {(trace: string) => Promise<object>} props.onSigne envoi de la signature
 * @returns {JSX.Element} le cartouche de signature
 */
export function SignatureContrat({ contrat, onSigne }) {
  const [accepte, setAccepte] = useState(false);
  const [trace, setTrace] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const signe = async () => {
    if (!accepte || !trace) return;
    setErreur('');
    setEnvoi(true);
    try {
      await onSigne(trace);
    } catch (e) {
      setErreur(e?.message || 'La signature n’a pas pu être enregistrée. Réessaie.');
    } finally {
      setEnvoi(false);
    }
  };

  if (!contrat) return null;

  if (!contrat.aSigner) {
    return (
      <div
        className="no-print"
        data-testid="contrat-signe"
        style={{
          background: C.greenL,
          borderBottom: '1px solid ' + C.green,
          padding: '10px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          fontSize: 11,
        }}
      >
        <div style={{ fontWeight: 800, color: C.green }}>
          ✓ Contrat signé le {dateHeureSuisse(contrat.signeLe)}
        </div>
        <BoutonDocument
          chemin="/mon-espace/contrat/signature/document"
          label="📄 Mon contrat signé (PDF)"
          color={C.green}
          sm
        />
      </div>
    );
  }

  return (
    <div
      className="no-print"
      data-testid="signer-contrat"
      style={{ background: '#fff', borderBottom: '1px solid ' + C.border, padding: '12px 14px' }}
    >
      <div style={{ fontSize: 13, fontWeight: 900, color: C.ink }}>
        {contrat.nouvelleSignatureDemandee
          ? '✍️ Ton contrat a changé : une nouvelle signature est demandée'
          : '✍️ Signer mon contrat'}
      </div>
      <div style={{ fontSize: 11, color: C.grey, lineHeight: 1.5, margin: '3px 0 9px' }}>
        {contrat.nouvelleSignatureDemandee
          ? 'La direction a modifié les conditions ci-dessous. Relis-les, puis signe de nouveau ; ton ancien contrat signé reste dans ton dossier.'
          : 'Lis ton contrat ci-dessous, puis coche, signe au doigt et valide. Le PDF signé sera rangé dans ton dossier.'}
      </div>
      <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={accepte}
          onChange={(e) => setAccepte(e.target.checked)}
          style={{ width: 18, height: 18, marginTop: 1 }}
        />
        <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.4 }}>
          J’ai lu et j’accepte ce contrat de travail.
        </span>
      </label>
      <div style={{ marginTop: 9 }}>
        <SignPad onChange={setTrace} />
      </div>
      {erreur && (
        <div role="alert" style={{ fontSize: 11, color: C.red, fontWeight: 700, marginTop: 6 }}>
          {erreur}
        </div>
      )}
      <button
        onClick={signe}
        disabled={!accepte || !trace || envoi}
        style={{
          marginTop: 9,
          width: '100%',
          background: !accepte || !trace ? C.muted : C.green,
          color: '#fff',
          border: 'none',
          borderRadius: 10,
          padding: '12px 14px',
          fontSize: 13,
          fontWeight: 800,
          cursor: !accepte || !trace || envoi ? 'not-allowed' : 'pointer',
        }}
      >
        {envoi ? 'Enregistrement…' : '✓ Signer mon contrat'}
      </button>
    </div>
  );
}

export function ContratEmploye({ emp, soc, onClose, contrat, onSigne }) {
  if (!emp) return null;
  const determinee = dureeDeterminee(emp);
  const salBase = Number(emp.salBase) || 0;
  const txH = Number(emp.txH) || 0;
  const horaire = phraseHoraire(emp);
  const journee = phraseJournee(emp);
  const nomComplet = [emp.prenom, emp.nom].filter(Boolean).join(' ');
  const _da = docAcc(soc),
    accInk = _da.accInk;
  const dd = (iso) => {
    if (!iso) return '—';
    const z = ('' + iso).split('-');
    return z.length === 3 ? z[2] + '.' + z[1] + '.' + z[0] : iso;
  };
  const dateEmis = new Date().toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const H2 = (t) => (
    <div
      style={{
        fontSize: 9.5,
        fontWeight: 800,
        color: accInk,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        margin: '9px 0 2px',
      }}
    >
      {t}
    </div>
  );
  const P = (c) => (
    <div style={{ fontSize: 10, color: '#2a3140', lineHeight: 1.5, marginBottom: 2 }}>{c}</div>
  );
  return (
    <div
      className="doc-root"
      style={{
        height: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: '#4a4e57',
      }}
    >
      <style>
        {
          '.a4-sheet{width:100%;max-width:210mm}@media(max-width:520px){.a4-wrap{padding:10px 6px!important}.a4-sheet{padding:22px 16px!important}}@media print{@page{size:A4;margin:0}html,body{background:#fff!important;margin:0!important;padding:0!important}.no-print{display:none!important}.doc-root{height:auto!important;overflow:visible!important;background:#fff!important}.a4-wrap{margin:0!important;padding:0!important;background:#fff!important}.a4-sheet{box-shadow:none!important;margin:0!important;width:210mm!important;max-width:210mm!important;min-height:auto!important;padding:12mm 14mm!important}}'
        }
      </style>
      <div
        className="no-print"
        style={{
          background: accInk,
          color: '#fff',
          padding: '12px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 5,
        }}
      >
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          ‹ Retour
        </button>
        <div style={{ display: 'flex', gap: 7 }}>
          <button
            onClick={printDoc}
            style={{
              background: C.green,
              border: 'none',
              color: '#fff',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 11,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            📄 PDF
          </button>
          <button
            onClick={printDoc}
            style={{
              background: 'rgba(255,255,255,.2)',
              border: '1px solid rgba(255,255,255,.45)',
              color: '#fff',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 11,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            🖨 Imprimer
          </button>
        </div>
      </div>
      {contrat && onSigne ? (
        <SignatureContrat contrat={contrat} onSigne={onSigne} />
      ) : (
        <div
          className="no-print"
          style={{
            background: C.tealL,
            color: C.grey,
            padding: '8px 14px',
            fontSize: 9,
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          📄 Voici votre contrat, en lecture seule. Pour toute modification, contactez la direction.
        </div>
      )}
      <div
        className="a4-wrap"
        style={{ padding: '18px 12px', display: 'flex', justifyContent: 'center' }}
      >
        <div
          className="a4-sheet"
          style={{
            width: '100%',
            maxWidth: '210mm',
            minHeight: '297mm',
            background: '#fff',
            color: '#0F1A2E',
            boxSizing: 'border-box',
            padding: '12mm 14mm',
            boxShadow: '0 6px 24px rgba(0,0,0,.35)',
            fontFamily: "'Helvetica Neue',Arial,sans-serif",
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '12px',
              paddingBottom: 9,
              borderBottom: '2px solid ' + accInk,
            }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              {(soc?.logo || SOC.logo) && (
                <img
                  src={soc?.logo || SOC.logo}
                  style={{ width: 46, height: 46, objectFit: 'contain' }}
                  alt=""
                />
              )}
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{soc?.nom || SOC.nom}</div>
                <div style={{ fontSize: 10, color: '#5a6270', marginTop: 2, lineHeight: 1.5 }}>
                  {soc?.adr || SOC.adr}
                  <br />
                  {soc?.npa || SOC.npa}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div
                style={{
                  display: 'inline-block',
                  background: accInk,
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 13,
                  padding: '6px 13px',
                  borderRadius: 6,
                }}
              >
                CONTRAT DE TRAVAIL
              </div>
              <div style={{ fontSize: 9, color: '#8a909c', marginTop: 6 }}>{dateEmis}</div>
            </div>
          </div>
          {(soc?.tva || SOC.tva) && (
            <div style={{ fontSize: 8.5, color: '#8a909c', padding: '7px 0 0' }}>
              N° TVA : {soc?.tva || SOC.tva}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              border: '1px solid #e2e5ea',
              borderRadius: 10,
              padding: '10px 13px',
              margin: '12px 0 4px',
              fontSize: 11,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 7.5,
                  color: '#8a909c',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                }}
              >
                Employé·e
              </div>
              <b style={{ fontSize: 13 }}>{nomComplet}</b>
              <div style={{ color: '#5a6270' }}>{emp.role || 'Collaborateur·trice'}</div>
            </div>
            <div style={{ textAlign: 'right', color: '#5a6270', fontSize: 10 }}>
              {emp.typeContrat && (
                <>
                  Contrat <b style={{ color: '#0F1A2E' }}>{emp.typeContrat}</b>
                  <br />
                </>
              )}
              {txH > 0 && (
                <>
                  Taux horaire{' '}
                  <b style={{ color: '#0F1A2E' }}>
                    {DEVISE} {fmt(txH)}/h
                  </b>
                  <br />
                </>
              )}
              {salBase > 0 && (
                <>
                  Salaire brut/mois{' '}
                  <b style={{ color: '#0F1A2E' }}>
                    {DEVISE} {fmt(salBase)}
                  </b>
                </>
              )}
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#2a3140', lineHeight: 1.6, margin: '8px 0 4px' }}>
            Entre <b>{soc?.nom || SOC.nom}</b> (ci-après « l'employeur ») et <b>{nomComplet}</b>{' '}
            (ci-après « l'employé·e »), il est convenu ce qui suit :
          </div>
          {H2('Art. 1 — Fonction')}
          {P(
            <>
              L'employé·e est engagé·e en qualité de{' '}
              <b>{emp.role || 'collaborateur ou collaboratrice'}</b>.
              {emp.lieu ? (
                <>
                  {' '}
                  Le lieu de travail est <b>{emp.lieu}</b>.
                </>
              ) : null}
            </>,
          )}
          {H2('Art. 2 — Entrée en fonction et durée')}
          {P(
            determinee ? (
              <>
                {emp.dateEmbauche ? (
                  <>
                    Les rapports de travail débutent le <b>{dd(emp.dateEmbauche)}</b>, pour une
                    durée déterminée jusqu'au <b>{dd(emp.dateFin)}</b>.
                  </>
                ) : (
                  <>
                    Les rapports de travail sont conclus pour une durée déterminée jusqu'au{' '}
                    <b>{dd(emp.dateFin)}</b>.
                  </>
                )}
              </>
            ) : (
              <>
                {emp.dateEmbauche ? (
                  <>
                    Les rapports de travail débutent le <b>{dd(emp.dateEmbauche)}</b>, pour une
                    durée indéterminée.
                  </>
                ) : (
                  <>Les rapports de travail sont conclus pour une durée indéterminée.</>
                )}
              </>
            ),
          )}
          {texteEssai(emp.essai, determinee) ? P(texteEssai(emp.essai, determinee)) : null}
          {/* Les articles suivent ceux du contrat officiel (PDF), dans le
              même ordre : l'employé signe le texte qu'il recevra. */}
          {H2('Art. 3 — Temps de travail')}
          {horaire ? P(horaire) : null}
          {journee ? P(journee) : null}
          {!horaire && !journee
            ? P(`L'employé·e travaille ${Number(emp.heuresJour) || 8} heures par jour.`)
            : null}
          {H2('Art. 4 — Salaire')}
          {P(
            salBase > 0 ? (
              <>
                Le salaire mensuel brut est de{' '}
                <b>
                  {DEVISE} {fmt(salBase)}
                </b>
                {txH > 0 ? (
                  <>
                    {' '}
                    (taux horaire de référence {DEVISE} {fmt(txH)}/h)
                  </>
                ) : null}
                , versé 12 fois l'an{emp.sal13 ? ', plus un 13e salaire' : ''}.
              </>
            ) : txH > 0 ? (
              <>
                Le salaire est fixé à l'heure :{' '}
                <b>
                  {DEVISE} {fmt(txH)}/h
                </b>{' '}
                brut, selon les heures effectuées
                {emp.sal13 ? ', plus un 13e salaire au prorata' : ''}.
              </>
            ) : (
              <>
                Le salaire n'est pas encore inscrit sur votre fiche : adressez-vous à la direction.
              </>
            ),
          )}
          {H2('Art. 5 — Vacances')}
          {P(
            <>
              L'employé·e a droit à <b>{emp.conges || 20} jours</b> de vacances payées par année
              civile. Le minimum légal de quatre semaines, cinq jusqu'à 20 ans révolus, demeure
              réservé (art. 329a CO).
            </>,
          )}
          {H2('Art. 6 — Assurances sociales')}
          {P(<>{assurancesDuContrat(emp)}</>)}
          {H2('Art. 7 — Résiliation')}
          {P(
            determinee ? (
              <>
                Le contrat prend fin le <b>{dd(emp.dateFin)}</b> sans qu'il soit nécessaire de le
                résilier (art. 334 CO)
                {emp.delaiResil ? (
                  <>
                    {' '}
                    ; avant ce terme, il peut être résilié moyennant un délai de{' '}
                    <b>{emp.delaiResil}</b>
                  </>
                ) : null}
                .
              </>
            ) : emp.delaiResil ? (
              <>
                Le contrat peut être résilié par chacune des parties moyennant un délai de{' '}
                <b>{emp.delaiResil}</b> (art. 335 ss CO).
              </>
            ) : (
              <>
                Après le temps d'essai, chaque partie peut résilier le contrat moyennant un délai
                d'un mois pendant la première année de service, de deux mois de la deuxième à la
                neuvième année, et de trois mois ensuite, pour la fin d'un mois (art. 335c CO).
              </>
            ),
          )}
          {H2('Art. 8 — Dispositions finales')}
          {P(dispositionsFinales(emp))}
          {String(emp.clausesContrat || '').trim() ? (
            <>
              {H2('Art. 9 — Clauses particulières')}
              <div
                style={{
                  fontSize: 10,
                  color: '#2a3140',
                  lineHeight: 1.5,
                  marginBottom: 2,
                  whiteSpace: 'pre-line',
                }}
              >
                {String(emp.clausesContrat).trim()}
              </div>
            </>
          ) : null}
          <div
            style={{
              marginTop: 28,
              paddingTop: 12,
              borderTop: '1px solid #dde1e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  borderTop: '1px solid #8a909c',
                  width: 150,
                  paddingTop: 5,
                  fontSize: 9,
                  color: '#8a909c',
                }}
              >
                L'employeur
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  borderTop: '1px solid #8a909c',
                  width: 150,
                  paddingTop: 5,
                  fontSize: 9,
                  color: '#8a909c',
                }}
              >
                L'employé·e
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Le temps d'essai, comme sur le PDF : celui convenu, trois mois au plus
 * (art. 335b CO) ; sans accord, le premier mois d'un contrat de durée
 * indéterminée.
 */
export function texteEssai(essai, determinee) {
  const v = String(essai || '').trim();
  if (!v)
    return determinee
      ? ''
      : tr(
          "Le premier mois constitue le temps d'essai (art. 335b CO) ; pendant celui-ci, chaque partie peut résilier le contrat moyennant un délai de sept jours.",
        );
  const bas = v.toLowerCase();
  if (/^0+\s*(mois)?$/.test(bas) || ['aucun', 'non', 'sans'].includes(bas)) {
    return tr("Aucun temps d'essai n'est convenu.");
  }
  const n = bas.match(/(\d+)/);
  const enMois = !bas.includes('semaine') && !bas.includes('jour');
  const duree = n && enMois && +n[1] > 3 ? tr('3 mois') : v;
  return trf(
    "Le temps d'essai est de {0}, trois mois au plus (art. 335b CO) ; pendant celui-ci, chaque partie peut résilier le contrat moyennant un délai de sept jours.",
    duree,
  );
}

/** Les assurances de la fiche, comme sur le PDF. */
export function assurancesDuContrat(emp) {
  const l = [];
  if (emp.soumisAVS !== false) l.push('AVS/AI/APG');
  if (emp.soumisAC !== false) l.push(tr('assurance-chômage (AC)'));
  if (emp.soumisLPP !== false) l.push(tr('prévoyance professionnelle (LPP)'));
  if (emp.soumisLAA !== false) l.push(tr('assurance-accidents (LAA)'));
  const base = l.length
    ? trf(
        "L'employé·e est assuré·e conformément à la loi : {0}. Les cotisations légales à sa charge sont déduites du salaire.",
        l.join(', '),
      )
    : tr(
        "L'employé·e n'est soumis·e à aucune assurance sociale par l'intermédiaire de l'employeur.",
      );
  return emp.soumisIS === true
    ? base + ' ' + tr("L'impôt à la source est retenu sur le salaire, selon le barème cantonal.")
    : base;
}
